import '@/Monitoring';
import 'simplebar-react/dist/simplebar.min.css';
import '@/stores/init';

import { handleRequestError, IgnoreError } from '@/error-handler';
import * as route from '@/constants/route';
import useUserStore from '@/stores/useUserStore';
import anewSignIn from '@/utils/anewSignIn';
import Axios from '@/utils/Axios';
import { useNotice } from '@/utils/notice';
import { injectGlobal } from '@emotion/css';
import { defineApp } from '@umijs/max';
import { App, ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { PropsWithChildren } from 'react';
import { SWRConfig } from 'swr';
import { hasSubject } from '@/utils/subject';
import useThemeStore from './stores/useThemeStore';
import { getProfile } from './requests/useProfile';
import Logger from './utils/Logger';
import isIpePlatform from './utils/isIpePlatform';

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
injectGlobal`
  #root {
    height: 100%;
  }
  
  input:-webkit-autofill,
  input:-webkit-autofill:focus {
    transition: background-color 0s 600000s, color 0s 600000s !important;
  }
`;

function Providers(props: PropsWithChildren) {
  const { children } = props;

  const antdThemeColor = useThemeStore((state) => state.antdThemeColor);

  return (
    <SWRConfig
      value={{
        shouldRetryOnError: false,
        revalidateOnFocus:
          process.env.UMI_APP_SWR_REVALIDATE_ON_FOCUS !== 'false',
        onError: (error) => {
          if (error.isAxiosError) {
            handleRequestError(error);
            throw new IgnoreError();
          }

          throw error;
        },
      }}
    >
      <ConfigProvider
        locale={zhCN}
        theme={{
          token: {
            colorPrimary: antdThemeColor || '#1677FF',
          },
          components: {
            Table: {
              headerSplitColor: 'transparent',
            },
            Select: {
              colorBorder: '#d9d9d986',
            },
          },
        }}
      >
        {children}
      </ConfigProvider>
    </SWRConfig>
  );
}

async function loadSchool() {
  const { data } = await Axios.get<
    {
      current: boolean;
      id: number;
      name: string;
      role: string;
    }[]
  >('/teachers/schools');

  useUserStore.setState({
    schools: data.map((v) => ({
      id: String(v.id),
      name: v.name,
    })),
    currentSchoolId: String(data.find((v) => v.current)?.id || ''),
  });
}

let alreadyLoadedUserProfile = false;

async function loadUserProfile() {
  if (alreadyLoadedUserProfile && useUserStore.getState().isFilled()) return;

  try {
    const profile = await getProfile();
    useUserStore.setState({
      id: String(profile.id),
      name: profile.name,
      prefer: profile.prefer || false,
      unit: profile.unit,
      section: profile.section,
    });

    await loadSchool();

    alreadyLoadedUserProfile = true;
  } catch (e) {
    Logger.debug(e, '加载用户信息失败');

    anewSignIn();
  }
}

function AppExtends() {
  useNotice();
  return null;
}

export default defineApp({
  rootContainer: (container) => (
    <Providers>
      <App>
        <AppExtends />
        {container}
      </App>
    </Providers>
  ),
  onRouteChange: () => {
    if (
      isIpePlatform() &&
      [
        route.classroomCourseware,
        route.intelligenceCourseware,
        route.intelligenceResource,
      ].some((v) => window.location.pathname.includes(v))
    ) {
      window.location.href = '/not-found';
    }

    if (
      ![new RegExp(`/${route.loginPrefix}`)].every((v) =>
        v.test(window.location.pathname),
      )
    ) {
      if (!hasSubject()) {
        anewSignIn('没有选择学科，请重新登录');
        return;
      }
    }

    if (localStorage.getItem('token') && !useUserStore.getState().isFilled()) {
      loadUserProfile();
    }
  },
  render: async (oldRender) => {
    if (localStorage.getItem('token')) {
      await loadUserProfile();
    }

    oldRender();
  },
});
